import React, { useState, useEffect, useMemo } from 'react';
import './EditContactForm.css';
import { formatPhoneNumberInput } from '../../utils/formatters';
import { apiFetch } from '../../utils/api';
import { useNavigate } from "react-router-dom"; // Make sure this is at the top of your component
import Snackbar from "../Snackbar";
import { useGlobalState } from "../../contexts/GlobalContext";
import { isRealtor } from "../../utils/userAccess";

const ROLE_OPTIONS = [
  { value: 'contact', label: 'Contact' },
  { value: 'customer', label: 'Customer' },
  { value: 'lead', label: 'Lead' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'investor', label: 'Investor' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'other', label: 'Other' },
];

const FINANCING_STATUS_OPTIONS = [
  { value: 'Pre-approved', label: 'Pre-Approved' },
  { value: 'Not approved', label: 'Seeking Approval' },
  { value: 'Cash buyer', label: 'Cash Buyer' },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Conventional', label: 'Conventional' },
  { value: 'FHA', label: 'FHA' },
  { value: 'VA', label: 'VA' },
  { value: 'USDA', label: 'USDA' },
  { value: 'Hard Money', label: 'Hard Money' },
  { value: 'Other', label: 'Other' },
];

const PREFERRED_CONTACT_METHOD_OPTIONS = [
  { value: 'Phone', label: 'Phone' },
  { value: 'Email', label: 'Email' },
  { value: 'Text', label: 'Text' },
  { value: 'In Person', label: 'In Person' },
];

const BEST_TIME_TO_CONTACT_OPTIONS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
];

const LEAD_SOURCE_OPTIONS = [
  { value: 'referral', label: 'Referral' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'advertisement', label: 'Advertisement' },
  { value: 'open_house', label: 'Open House' },
  { value: 'floor', label: 'Floor' },
  { value: 'cold call', label: 'Cold Call' },
  { value: 'other', label: 'Other' },
];

const PROSPECT_STAGE_OPTIONS = [
  { value: 'New', label: 'New' },
  { value: 'Attempting Contact', label: 'Attempting Contact' },
  { value: 'Connected', label: 'Connected' },
  { value: 'Qualified', label: 'Qualified' },
  { value: 'Nurture', label: 'Nurture' },
  { value: 'Scheduled Follow-up', label: 'Scheduled Follow-up' },
  { value: 'Under Contract', label: 'Under Contract' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Lost', label: 'Lost' },
  { value: 'Do Not Call', label: 'Do Not Call' },
  { value: 'Unreachable', label: 'Unreachable' },
];

const OWNER_PROPERTY_TYPE_OPTIONS = [
  { value: 'Single-Family', label: 'Single-Family' },
  { value: 'Townhouse', label: 'Townhouse' },
  { value: 'Condo', label: 'Condo' },
  { value: 'Land', label: 'Land' },
  { value: 'Commercial', label: 'Commercial' },
];

const OCCUPANCY_OPTIONS = [
  { value: 'Owner-occupied', label: 'Owner-occupied' },
  { value: 'Rental', label: 'Rental' },
  { value: 'Vacant', label: 'Vacant' },
];

const TIMELINE_TO_PURCHASE_OPTIONS = [
  { value: 'ASAP', label: 'ASAP' },
  { value: '0-30 days', label: '0-30 days' },
  { value: '30-60 days', label: '30-60 days' },
  { value: '60-90 days', label: '60-90 days' },
  { value: '3-6 months', label: '3-6 months' },
  { value: '6-12 months', label: '6-12 months' },
  { value: '12+ months', label: '12+ months' },
  { value: 'Just researching', label: 'Just researching' },
];

const DECISION_ROLE_OPTIONS = [
  { value: 'Decision Maker', label: 'Decision Maker' },
  { value: 'Influencer', label: 'Influencer' },
  { value: 'Evaluator', label: 'Evaluator' },
  { value: 'User', label: 'User' },
  { value: 'Unknown', label: 'Unknown' },
];

const URGENCY_LEVEL_OPTIONS = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];

const PREAPPROVAL_STATUS_OPTIONS = [
  { value: 'Not started', label: 'Not started' },
  { value: 'Lender shopping', label: 'Lender shopping' },
  { value: 'Pre-qualified', label: 'Pre-qualified' },
  { value: 'Pre-approved', label: 'Pre-approved' },
  { value: 'Underwritten approval', label: 'Underwritten approval' },
  { value: 'Cash buyer', label: 'Cash buyer' },
];

const FUTURE_USE_OPTIONS = [
  { value: 'Primary Residence', label: 'Primary Residence' },
  { value: 'Secondary Residence', label: 'Secondary Residence' },
  { value: 'Rental', label: 'Rental / Investment' },
  { value: 'Fix & Flip', label: 'Fix & Flip' },
  { value: 'Development', label: 'Development' },
  { value: 'Vacation', label: 'Vacation Home' },
  { value: 'Other', label: 'Other' },
];

const COMMISSION_OPTIONS = Array.from({ length: 10 }, (_, idx) => {
  const percent = String(idx + 1);
  return { value: percent, label: `${percent}%` };
});

const TERM_OPTIONS = [
  { value: '30 days', label: '30 days' },
  { value: '45 days', label: '45 days' },
  { value: '60 days', label: '60 days' },
  { value: '90 days', label: '90 days' },
  { value: '120 days', label: '120 days' },
];

const US_STATE_OPTIONS = [
  { value: 'AL', label: 'AL — Alabama' },
  { value: 'AK', label: 'AK — Alaska' },
  { value: 'AZ', label: 'AZ — Arizona' },
  { value: 'AR', label: 'AR — Arkansas' },
  { value: 'CA', label: 'CA — California' },
  { value: 'CO', label: 'CO — Colorado' },
  { value: 'CT', label: 'CT — Connecticut' },
  { value: 'DE', label: 'DE — Delaware' },
  { value: 'FL', label: 'FL — Florida' },
  { value: 'GA', label: 'GA — Georgia' },
  { value: 'HI', label: 'HI — Hawaii' },
  { value: 'ID', label: 'ID — Idaho' },
  { value: 'IL', label: 'IL — Illinois' },
  { value: 'IN', label: 'IN — Indiana' },
  { value: 'IA', label: 'IA — Iowa' },
  { value: 'KS', label: 'KS — Kansas' },
  { value: 'KY', label: 'KY — Kentucky' },
  { value: 'LA', label: 'LA — Louisiana' },
  { value: 'ME', label: 'ME — Maine' },
  { value: 'MD', label: 'MD — Maryland' },
  { value: 'MA', label: 'MA — Massachusetts' },
  { value: 'MI', label: 'MI — Michigan' },
  { value: 'MN', label: 'MN — Minnesota' },
  { value: 'MS', label: 'MS — Mississippi' },
  { value: 'MO', label: 'MO — Missouri' },
  { value: 'MT', label: 'MT — Montana' },
  { value: 'NE', label: 'NE — Nebraska' },
  { value: 'NV', label: 'NV — Nevada' },
  { value: 'NH', label: 'NH — New Hampshire' },
  { value: 'NJ', label: 'NJ — New Jersey' },
  { value: 'NM', label: 'NM — New Mexico' },
  { value: 'NY', label: 'NY — New York' },
  { value: 'NC', label: 'NC — North Carolina' },
  { value: 'ND', label: 'ND — North Dakota' },
  { value: 'OH', label: 'OH — Ohio' },
  { value: 'OK', label: 'OK — Oklahoma' },
  { value: 'OR', label: 'OR — Oregon' },
  { value: 'PA', label: 'PA — Pennsylvania' },
  { value: 'RI', label: 'RI — Rhode Island' },
  { value: 'SC', label: 'SC — South Carolina' },
  { value: 'SD', label: 'SD — South Dakota' },
  { value: 'TN', label: 'TN — Tennessee' },
  { value: 'TX', label: 'TX — Texas' },
  { value: 'UT', label: 'UT — Utah' },
  { value: 'VT', label: 'VT — Vermont' },
  { value: 'VA', label: 'VA — Virginia' },
  { value: 'WA', label: 'WA — Washington' },
  { value: 'WV', label: 'WV — West Virginia' },
  { value: 'WI', label: 'WI — Wisconsin' },
  { value: 'WY', label: 'WY — Wyoming' },
  { value: 'DC', label: 'DC — District of Columbia' },
  { value: 'PR', label: 'PR — Puerto Rico' },
];

const CUSTOM_OPTION_VALUE = '__custom__';

const DROPDOWN_FIELD_CONFIG = {
  mailSt: {
    defaults: US_STATE_OPTIONS,
    dataKeys: ['mailSt', 'mail_state'],
  },
  best_time_to_contact: {
    defaults: BEST_TIME_TO_CONTACT_OPTIONS,
    dataKeys: ['best_time_to_contact', 'time'],
  },
  priority: {
    defaults: [
      { value: 'High', label: 'High' },
      { value: 'Medium', label: 'Medium' },
      { value: 'Low', label: 'Low' },
    ],
    dataKeys: ['priority'],
  },
  financing_plan: {
    defaults: FINANCING_STATUS_OPTIONS,
    dataKeys: ['financing_plan', 'financing_status'],
  },
  paymentType: {
    defaults: PAYMENT_TYPE_OPTIONS,
    dataKeys: ['paymentType', 'payment_type'],
  },
  lead_source: {
    defaults: LEAD_SOURCE_OPTIONS,
    dataKeys: ['lead_source', 'lead_source_detail'],
  },
  preferred_contact_method: {
    defaults: PREFERRED_CONTACT_METHOD_OPTIONS,
    dataKeys: ['preferred_contact_method', 'preferredContactMethod'],
  },
  prospect_stage: {
    defaults: PROSPECT_STAGE_OPTIONS,
    dataKeys: ['prospect_stage'],
  },
  decision_role: {
    defaults: DECISION_ROLE_OPTIONS,
    dataKeys: ['decision_role'],
  },
  urgency_level: {
    defaults: URGENCY_LEVEL_OPTIONS,
    dataKeys: ['urgency_level'],
  },
  timeline_to_purchase: {
    defaults: TIMELINE_TO_PURCHASE_OPTIONS,
    dataKeys: ['timeline_to_purchase'],
  },
  preapproval_status: {
    defaults: PREAPPROVAL_STATUS_OPTIONS,
    dataKeys: ['preapproval_status'],
  },
  owner_property_type: {
    defaults: OWNER_PROPERTY_TYPE_OPTIONS,
    dataKeys: ['owner_property_type'],
  },
  occupancy: {
    defaults: OCCUPANCY_OPTIONS,
    dataKeys: ['occupancy'],
  },
  current_property_state: {
    defaults: US_STATE_OPTIONS,
    dataKeys: ['current_property_state'],
  },
  futureUse: {
    defaults: FUTURE_USE_OPTIONS,
    dataKeys: ['futureUse', 'future_use'],
  },
  commission: {
    defaults: COMMISSION_OPTIONS,
    dataKeys: ['commission'],
  },
  term: {
    defaults: TERM_OPTIONS,
    dataKeys: ['term'],
  },
};

const DROPDOWN_FIELD_NAMES = new Set(Object.keys(DROPDOWN_FIELD_CONFIG));
const ADDITIONAL_DROPDOWN_FIELDS = new Set(
  Object.keys(DROPDOWN_FIELD_CONFIG).filter((field) => field !== 'mailSt')
);

const getNestedValue = (entity, path) => {
  if (!entity || !path) return null;
  if (!path.includes('.')) return entity?.[path];
  return path.split('.').reduce((acc, segment) => (acc ? acc[segment] : undefined), entity);
};

const addOptionIfNeeded = (mapRef, value, label) => {
  if (value === null || value === undefined) return;
  const normalized = String(value).trim();
  if (!normalized) return;
  const key = normalized.toLowerCase();
  if (mapRef.has(key)) return;
  mapRef.set(key, { value: normalized, label: label || normalized });
};

const buildDropdownOptions = (contacts = []) => {
  const optionsMap = {};

  Object.entries(DROPDOWN_FIELD_CONFIG).forEach(([field, config]) => {
    const optionStore = new Map();

    (config.defaults || []).forEach((opt) =>
      addOptionIfNeeded(optionStore, opt.value, opt.label)
    );

    contacts.forEach((contact) => {
      (config.dataKeys || []).forEach((key) => {
        const value = getNestedValue(contact, key);
        if (value !== undefined && value !== null && value !== '') {
          addOptionIfNeeded(optionStore, value);
        }
      });
    });

    optionsMap[field] = Array.from(optionStore.values());
  });

  return optionsMap;
};

const buildLeadSourceOptions = (contacts = []) => {
  const optionStore = new Map();

  LEAD_SOURCE_OPTIONS.forEach((opt) =>
    addOptionIfNeeded(optionStore, opt.value, opt.label)
  );

  contacts.forEach((contact) => {
    const detail = getNestedValue(contact, 'lead_source_detail');
    if (detail !== undefined && detail !== null && detail !== '') {
      addOptionIfNeeded(optionStore, `custom:${detail}`, detail);
    }
  });

  return Array.from(optionStore.values());
};

const optionMatchesValue = (options = [], value) => {
  if (!value) return false;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return false;
  return options.some(
    (opt) => String(opt.value).trim().toLowerCase() === normalized
  );
};


function EditContactForm({ contactData, onSave }) {
  const navigate = useNavigate(); // Inside your component
  const { state } = useGlobalState();
  const realtorView = isRealtor(state.user);
  const dropdownOptions = useMemo(
    () => buildDropdownOptions(state.contacts || []),
    [state.contacts]
  );
  const leadSourceOptions = useMemo(
    () => buildLeadSourceOptions(state.contacts || []),
    [state.contacts]
  );

  const additionalFieldOptions = [
    { name: 'company_name', label: 'Company Name' },
    { name: 'job_title', label: 'Job Title' },
    { name: 'industry', label: 'Industry' },
    { name: 'decision_role', label: 'Decision Role' },
    { name: 'current_solution', label: 'Current Solution' },
    { name: 'urgency_level', label: 'Urgency Level' },
    { name: 'next_step', label: 'Next Step' },
    { name: 'estimated_value', label: 'Estimated Value' },
    { name: 'best_time_to_contact', label: 'Best Time to Contact' },
    { name: 'priority', label: 'Priority' },
    { name: 'lead_source', label: 'Prospect Source' },
    { name: 'llcs', label: 'LLC(s)' },
    { name: 'pain_points', label: 'Pain Points' },
    { name: 'goals', label: 'Goals' },
    { name: 'budget', label: 'Budget' },
    { name: 'motivation', label: 'Motivation' },
    { name: 'preapproval_status', label: 'Preapproval Status' },
    { name: 'commission', label: 'Commission' },
    { name: 'co_buyer_name', label: 'Co-Buyer First Name' },
    { name: 'co_buyer_last_name', label: 'Co-Buyer Last Name' },
    { name: 'co_buyer_email', label: 'Co-Buyer Email' },
    { name: 'co_buyer_phone', label: 'Co-Buyer Phone' },
    { name: 'current_property_street', label: 'Current Property Street' },
    { name: 'current_property_city', label: 'Current Property City' },
    { name: 'current_property_state', label: 'Current Property State' },
    { name: 'current_property_zip', label: 'Current Property Zip' },
    { name: 'daysForClosing', label: 'Days for Closing' },
    { name: 'down_payment', label: 'Down Payment' },
    { name: 'dueDiligence', label: 'Due Diligence Period' },
    { name: 'escrowAmount', label: 'Escrow Amount' },
    { name: 'financing_plan', label: 'Financing Plan' },
    { name: 'home_features', label: 'Home Features' },
    { name: 'link', label: 'External Link' },
    { name: 'notes', label: 'Additional Notes' },
    { name: 'offer', label: 'Offer Amount' },
    { name: 'occupancy', label: 'Occupancy' },
    { name: 'owner_property_type', label: 'Current Property Type' },
    { name: 'preferred_contact_method', label: 'Preferred Contact Method' },
    { name: 'preferred_neighborhoods', label: 'Preferred Neighborhoods' },
    { name: 'prospect_stage', label: 'Prospect Stage' },
    { name: 'next_follow_up_at', label: 'Next Follow-Up' },
    { name: 'propertyType', label: 'Property Type Sought' },
    { name: 'sellerA', label: 'Seller A' },
    { name: 'sellerB', label: 'Seller B Email' },
    { name: 'term', label: 'Term' },
    { name: 'timeline_to_purchase', label: 'Timeline To Purchase' },
    { name: 'titleCompany', label: 'Title Company' },
    { name: 'prospect_property', label: 'Prospecting Property' },
  ];
  const propertyRelatedFieldNames = new Set([
    'current_property_street',
    'current_property_city',
    'current_property_state',
    'current_property_zip',
    'owner_property_type',
    'occupancy',
    'preapproval_status',
    'propertyType',
    'prospect_property',
    'home_features',
    'financing_plan',
    'down_payment',
    'preferred_neighborhoods',
  ]);
  const PROPERTY_TYPE_OPTIONS = [
    {
      value: 'Residential',
      label: 'Residential',
      subtypes: [
        'Single Family',
        'Multi-Family',
        'Condo / Townhome',
        'Mobile / Manufactured',
        'Vacation / Second Home',
        'Other Residential',
      ],
    },
    {
      value: 'Commercial',
      label: 'Commercial',
      subtypes: [
        'Retail',
        'Office',
        'Industrial',
        'Mixed-Use',
        'Hospitality',
        'Other Commercial',
      ],
    },
    {
      value: 'Land',
      label: 'Land',
      subtypes: [
        'Residential Lot',
        'Commercial Lot',
        'Agricultural',
        'Recreational',
        'Timber',
        'Other Land',
      ],
    },
  ];

  const normalizeOptionValue = (value) => {
    if (!value) return '';
    if (Array.isArray(value)) return value[0] || '';
    return value;
  };

  const getSubtypeOptions = (type) => {
    const match = PROPERTY_TYPE_OPTIONS.find((opt) => opt.value === type);
    return match ? match.subtypes : [];
  };

  const matchPropertyType = (value) => {
    const normalized = normalizeOptionValue(value);
    if (!normalized) return '';
    const match = PROPERTY_TYPE_OPTIONS.find(
      (opt) => opt.value.toLowerCase() === String(normalized).toLowerCase()
    );
    return match ? match.value : normalized;
  };

  const matchPropertySubtype = (type, value) => {
    const normalizedType = matchPropertyType(type);
    const normalizedSubtype = normalizeOptionValue(value);
    if (!normalizedSubtype) return '';
    const subtypeOptions = getSubtypeOptions(normalizedType);
    const match = subtypeOptions.find(
      (opt) => opt.toLowerCase() === String(normalizedSubtype).toLowerCase()
    );
    return match || normalizedSubtype;
  };

  const getFieldValue = (data, fieldName) => {
    switch (fieldName) {
      case 'best_time_to_contact':
        return data.best_time_to_contact ?? data.time ?? '';
      case 'lead_source':
      case 'company_name':
      case 'job_title':
      case 'industry':
      case 'decision_role':
      case 'current_solution':
      case 'urgency_level':
      case 'next_step':
      case 'estimated_value':
      case 'lead_source_detail':
      case 'llcs':
      case 'pain_points':
      case 'goals':
      case 'budget':
      case 'motivation':
      case 'preapproval_status':
      case 'commission':
      case 'co_buyer_email':
      case 'co_buyer_phone':
      case 'current_property_street':
      case 'current_property_city':
      case 'current_property_state':
      case 'current_property_zip':
      case 'down_payment':
      case 'link':
      case 'notes':
      case 'offer':
      case 'occupancy':
      case 'owner_property_type':
      case 'preferred_contact_method':
      case 'preferred_neighborhoods':
      case 'prospect_stage':
      case 'next_follow_up_at':
      case 'timeline_to_purchase':
      case 'term':
      case 'prospect_street':
      case 'prospect_city':
      case 'prospect_state':
      case 'prospect_zip':
      case 'acreage':
      case 'min_bedrooms':
      case 'min_bathrooms':
      case 'parcel_number':
        return data[fieldName] ?? '';
      case 'propertyType':
        return data.propertyType ?? data.property_type ?? '';
      case 'home_features':
        return data.home_features ?? data.wants_needs ?? data.wantsNeeds ?? '';
      case 'financing_plan':
        return data.financing_plan ?? data.financing_status ?? data.financingStatus ?? '';
      case 'titleCompany':
        return data.titleCompany ?? data.title_company ?? '';
      case 'dueDiligence':
        return data.dueDiligence ?? data.due_diligence ?? '';
      case 'daysForClosing':
        return data.daysForClosing ?? data.days_for_closing ?? '';
      case 'escrowAmount':
        return data.escrowAmount ?? data.escrow_amount ?? '';
      case 'sellerA':
        return data.sellerA ?? data.seller_a ?? '';
      case 'sellerB':
        return data.sellerB ?? data.seller_b ?? '';
      case 'co_buyer_name':
        return data.co_buyer_name ?? data.seller_a ?? '';
      case 'co_buyer_last_name':
        return data.co_buyer_last_name ?? data.seller_b ?? '';
      default:
        return data[fieldName] ?? '';
    }
  };

  const getExtraFieldsFromData = (data) =>
    additionalFieldOptions
      .filter((field) => {
        if (field.name === 'prospect_property') {
          return [
            data.prospect_street,
            data.prospect_city,
            data.prospect_state,
            data.prospect_zip,
            data.parcel_number,
            data.acreage,
            data.min_bedrooms,
            data.min_bathrooms,
          ].some(Boolean);
        }
        const value = getFieldValue(data, field.name);
        return value !== null && value !== undefined && value !== '';
      })
      .map((field) => field.name);

  const mapContactToForm = (data) => {
    const mappedType = matchPropertyType(data.propertyType ?? data.property_type);
    const legacyNameParts = String(data.name || '').trim().split(/\s+/).filter(Boolean);
    const firstName = data.first_name ?? legacyNameParts[0] ?? '';
    const lastName = data.last_name ?? legacyNameParts.slice(1).join(' ');
    const nextFollowUpAt = data.next_follow_up_at
      ? String(data.next_follow_up_at).replace(' ', 'T').slice(0, 16)
      : '';
    return {
      ...data,
      id: data.id ?? data.contact_id,
      first_name: firstName,
      last_name: lastName,
      mailAddress: data.mailAddress ?? data.mail_street ?? '',
      mailCity: data.mailCity ?? data.mail_city ?? '',
      mailSt: data.mailSt ?? data.mail_state ?? '',
      mailZip: data.mailZip ?? data.mail_zip ?? '',
      best_time_to_contact: data.best_time_to_contact ?? data.time ?? '',
      priority: data.priority ?? '',
      company_name: data.company_name ?? '',
      job_title: data.job_title ?? '',
      industry: data.industry ?? '',
      decision_role: data.decision_role ?? '',
      current_solution: data.current_solution ?? '',
      urgency_level: data.urgency_level ?? '',
      next_step: data.next_step ?? '',
      estimated_value: data.estimated_value ?? '',
      lead_source: data.lead_source ?? '',
      lead_source_detail: data.lead_source_detail ?? '',
      pain_points: data.pain_points ?? '',
      goals: data.goals ?? '',
      motivation: data.motivation ?? '',
      preapproval_status: data.preapproval_status ?? '',
      co_buyer_name: data.co_buyer_name ?? data.seller_a ?? '',
      co_buyer_last_name: data.co_buyer_last_name ?? data.seller_b ?? '',
      co_buyer_email: data.co_buyer_email ?? '',
      co_buyer_phone: data.co_buyer_phone ?? '',
      owner_property_type: data.owner_property_type ?? '',
      occupancy: data.occupancy ?? '',
      current_property_street: data.current_property_street ?? '',
      current_property_city: data.current_property_city ?? '',
      current_property_state: data.current_property_state ?? '',
      current_property_zip: data.current_property_zip ?? '',
      home_features: data.home_features ?? data.wants_needs ?? '',
      financing_plan: data.financing_plan ?? data.financing_status ?? '',
      down_payment: data.down_payment ?? '',
      preferred_neighborhoods: data.preferred_neighborhoods ?? '',
      propertyType: mappedType,
      propertySubtype: matchPropertySubtype(
        mappedType,
        data.propertySubtype ?? data.property_subtype
      ),
      next_follow_up_at: nextFollowUpAt,
    };
  };

  const [formState, setFormState] = useState(mapContactToForm(contactData));
  const [roleType, setRoleType] = useState(contactData.role || 'contact');

  const prospectFieldsFilled = [
    contactData.prospect_street,
    contactData.prospect_city,
    contactData.prospect_state,
    contactData.prospect_zip,
    contactData.parcel_number,
    contactData.acreage,
    contactData.min_bedrooms,
    contactData.min_bathrooms,
  ].some(Boolean);

  const [extraFields, setExtraFields] = useState(getExtraFieldsFromData(mapContactToForm(contactData)));
  const [customFieldOverrides, setCustomFieldOverrides] = useState({});
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    const mappedContact = mapContactToForm(contactData);
    setFormState(mappedContact);
    setRoleType(contactData.role || 'contact');
    setExtraFields(getExtraFieldsFromData(mappedContact));
    setCustomFieldOverrides({});
  }, [contactData]);

  useEffect(() => {
    const contactId = contactData?.id || contactData?.contact_id;
    if (!contactId) {
      return;
    }

    let isMounted = true;

    const fetchFullContact = async () => {
      try {
        const result = await apiFetch('getFullContactDetails', {
          queryParams: { contact_id: contactId },
        });

        if (!isMounted || !result?.success) {
          return;
        }

        const mergedContact = mapContactToForm({
          ...result.contact,
          ...result.preferences,
          ...(result.transactions || {}),
          properties: result.properties || [],
          tasks: result.tasks || [],
          activities: result.activities || [],
        });

        setFormState(mergedContact);
        setRoleType(mergedContact.role || 'contact');
        setExtraFields(getExtraFieldsFromData(mergedContact));
      } catch (error) {
      }
    };

    fetchFullContact();

    return () => {
      isMounted = false;
    };
  }, [contactData?.id, contactData?.contact_id]);

  const shouldDisplayField = (fieldName) => {
    if (fieldName === 'propertyType') {
      return extraFields.includes(fieldName) || !!formState.propertyType || !!formState.propertySubtype;
    }
    if (fieldName === 'best_time_to_contact') {
      return extraFields.includes(fieldName) || !!formState.best_time_to_contact;
    }
    if (fieldName === 'financing_plan') {
      return extraFields.includes(fieldName) || !!formState.financing_plan;
    }
    if (fieldName === 'home_features') {
      return extraFields.includes(fieldName) || !!formState.home_features;
    }
    return extraFields.includes(fieldName) || !!formState[fieldName];
  };

  const availableFieldsToAdd = additionalFieldOptions.filter(
    (field) => (realtorView || !propertyRelatedFieldNames.has(field.name)) && !shouldDisplayField(field.name)
  );

  const clearCustomOverride = (field) => {
    setCustomFieldOverrides((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleCustomValueChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'propertyType') {
      setFormState((prev) => {
        const safeValue = value;
        const subtypeOptions = getSubtypeOptions(safeValue);
        const nextSubtype = subtypeOptions.includes(prev.propertySubtype) ? prev.propertySubtype : '';
        return {
          ...prev,
          propertyType: safeValue,
          propertySubtype: nextSubtype,
        };
      });
      return;
    }

    if (name === 'lead_source') {
      if (value.startsWith('custom:')) {
        setFormState((prev) => ({
          ...prev,
          lead_source: 'other',
          lead_source_detail: value.slice('custom:'.length),
        }));
        return;
      }

      setFormState((prev) => ({
        ...prev,
        lead_source: value,
        lead_source_detail: value === 'other' ? prev.lead_source_detail || '' : '',
      }));
      return;
    }

    if (DROPDOWN_FIELD_NAMES.has(name)) {
      if (value === CUSTOM_OPTION_VALUE) {
        setCustomFieldOverrides((prev) => ({ ...prev, [name]: true }));
        setFormState((prev) => ({ ...prev, [name]: '' }));
        return;
      }
      clearCustomOverride(name);
    }

    const newValue = name === "phone" ? formatPhoneNumberInput(value) : value;
    setFormState((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleRoleChange = (event) => {
    setRoleType(event.target.value);
  };

  const handleExtraFieldToggle = (field) => {
    setExtraFields((prevFields) => {
      if (prevFields.includes(field)) {
      if (field === 'propertyType') {
        setFormState((prev) => ({ ...prev, propertyType: '', propertySubtype: '' }));
      }
      if (field === 'financing_plan') {
        setFormState((prev) => ({ ...prev, financing_plan: '', down_payment: '' }));
      }
      if (field === 'current_property_state') {
        clearCustomOverride('current_property_state');
      }
        if (field === 'prospect_property') {
          setFormState((prev) => ({
            ...prev,
            prospect_street: '',
            prospect_city: '',
            prospect_state: '',
            prospect_zip: '',
            parcel_number: '',
            acreage: '',
            min_bedrooms: '',
            min_bathrooms: ''
          }));
        }
        if (DROPDOWN_FIELD_NAMES.has(field)) {
          clearCustomOverride(field);
        }
        return prevFields.filter((f) => f !== field);
      }

      if (field === 'propertyType') {
        setFormState((prev) => ({ ...prev, propertyType: prev.propertyType || '', propertySubtype: '' }));
      }

      return [...prevFields, field];
    });
  };

  const renderDynamicField = (fieldName) => {
    const fieldConfig = additionalFieldOptions.find((f) => f.name === fieldName);
    if (!fieldConfig) return null;
    if (fieldName === 'prospect_property') return null;
    const selectOptions = dropdownOptions[fieldName] || [];
    const isDropdownField =
      ADDITIONAL_DROPDOWN_FIELDS.has(fieldName) && selectOptions.length > 0;

    if (fieldName === 'propertyType') {
      const subtypeOptions = getSubtypeOptions(formState.propertyType);
      return (
        <React.Fragment key={fieldName}>
          <div className="col-md-4 mb-3">
            <label className="form-label">{fieldConfig.label}</label>
            <select
              className="form-select"
              name="propertyType"
              value={formState.propertyType || ''}
              onChange={handleInputChange}
            >
              <option value="">Select type</option>
              {PROPERTY_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Property Subtype</label>
            <select
              className="form-select"
              name="propertySubtype"
              value={formState.propertySubtype || ''}
              onChange={handleInputChange}
              disabled={!formState.propertyType}
            >
              <option value="">
                {formState.propertyType ? 'Select subtype' : 'Select property type first'}
              </option>
              {subtypeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </React.Fragment>
      );
    }

    if (fieldName === 'next_follow_up_at') {
      return (
        <div className="col-md-4 mb-3" key={fieldName}>
          <label className="form-label">{fieldConfig.label}</label>
          <input
            type="datetime-local"
            className="form-control"
            name={fieldName}
            value={formState[fieldName] || ''}
            onChange={handleInputChange}
          />
        </div>
      );
    }

    if (fieldName === 'lead_source') {
      const selectValue =
        formState.lead_source === 'other' && formState.lead_source_detail
          ? `custom:${formState.lead_source_detail}`
          : (formState.lead_source || '');

      return (
        <div className="col-md-4 mb-3" key={fieldName}>
          <label className="form-label">{fieldConfig.label}</label>
          <select
            className="form-select"
            name="lead_source"
            value={selectValue}
            onChange={handleInputChange}
          >
            <option value="">Select {fieldConfig.label}</option>
            {leadSourceOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {formState.lead_source === 'other' && (
            <input
              type="text"
              className="form-control mt-2"
              name="lead_source_detail"
              value={formState.lead_source_detail || ''}
              onChange={handleInputChange}
              placeholder="Describe the source"
            />
          )}
        </div>
      );
    }

    if (
      fieldName === 'notes' ||
      fieldName === 'current_solution' ||
      fieldName === 'home_features' ||
      fieldName === 'preferred_neighborhoods' ||
      fieldName === 'pain_points' ||
      fieldName === 'goals'
    ) {
      return (
        <div className="col-md-6 mb-3" key={fieldName}>
          <label className="form-label">{fieldConfig.label}</label>
          <textarea
            className="form-control"
            name={fieldName}
            value={formState[fieldName] || ''}
            onChange={handleInputChange}
            rows={fieldName === 'notes' ? 4 : 2}
          />
        </div>
      );
    }

    if (isDropdownField) {
      const currentValue = formState[fieldName] || '';
      const isCustomValue =
        customFieldOverrides[fieldName] ||
        (currentValue && !optionMatchesValue(selectOptions, currentValue));
      const selectValue = isCustomValue ? CUSTOM_OPTION_VALUE : currentValue;
      return (
        <div className="col-md-4 mb-3" key={fieldName}>
          <label className="form-label">{fieldConfig.label}</label>
          <select
            className="form-select"
            name={fieldName}
            value={selectValue}
            onChange={handleInputChange}
          >
            <option value="">Select {fieldConfig.label}</option>
            {selectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
            <option value={CUSTOM_OPTION_VALUE}>Custom</option>
          </select>
          {isCustomValue && (
            <input
              type="text"
              className="form-control mt-2"
              value={currentValue}
              onChange={(e) => handleCustomValueChange(fieldName, e.target.value)}
              placeholder={`Enter ${fieldConfig.label.toLowerCase()}`}
            />
          )}
        </div>
      );
    }

    return (
      <div className="col-md-4 mb-3" key={fieldName}>
        <label className="form-label">{fieldConfig.label}</label>
        <input
          type={
            fieldName === 'budget' || fieldName === 'offer' || fieldName === 'escrowAmount' || fieldName === 'down_payment' || fieldName === 'estimated_value'
              ? 'number'
              : fieldName === 'co_buyer_email'
                ? 'email'
                : fieldName === 'co_buyer_phone'
                  ? 'tel'
                  : 'text'
          }
          className="form-control"
          name={fieldName}
          value={formState[fieldName] || ''}
          onChange={handleInputChange}
        />
      </div>
    );
  };

  const renderFieldGroup = (title, fields) => {
    const visibleFields = fields.filter((fieldName) => shouldDisplayField(fieldName));
    if (visibleFields.length === 0) {
      return null;
    }

    return (
      <React.Fragment key={title}>
        <div className="col-12 mt-3">
          <h5>{title}</h5>
        </div>
        {visibleFields.map((fieldName) => renderDynamicField(fieldName))}
      </React.Fragment>
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const contactId = formState.id || formState.contact_id;

    const propertyTypeValue = Array.isArray(formState.propertyType)
      ? formState.propertyType[0]
      : formState.propertyType || null;

    const propertySubtypeValue = Array.isArray(formState.propertySubtype)
      ? formState.propertySubtype[0]
      : formState.propertySubtype || null;

    const payload = {
      contact_id: contactId,
      first_name: formState.first_name?.trim() || "",
      last_name: formState.last_name?.trim() || "",
      llcs: formState.llcs || null,
      email: formState.email,
      phone: formState.phone,
      role: roleType,
      user_id: formState.user_id,
      mail_street: formState.mailAddress,
      mail_city: formState.mailCity,
      mail_state: formState.mailSt,
      mail_zip: formState.mailZip,
      company_name: formState.company_name || null,
      job_title: formState.job_title || null,
      industry: formState.industry || null,
      decision_role: formState.decision_role || null,
      current_solution: formState.current_solution || null,
      urgency_level: formState.urgency_level || null,
      next_step: formState.next_step || null,
      estimated_value: formState.estimated_value || null,
      budget: formState.budget || null,
      interest_street: formState.interestAddress || null,
      time: formState.best_time_to_contact || null,
      priority: formState.priority || null,
      lead_source: formState.lead_source || null,
      lead_source_detail: formState.lead_source === 'other' ? (formState.lead_source_detail || null) : null,
      pain_points: formState.pain_points || null,
      goals: formState.goals || null,
      motivation: formState.motivation || null,
      preapproval_status: formState.preapproval_status || null,
      preferred_contact_method: formState.preferred_contact_method || null,
      prospect_stage: formState.prospect_stage || null,
      next_follow_up_at: formState.next_follow_up_at || null,
      timeline_to_purchase: formState.timeline_to_purchase || null,
      financing_status: formState.financing_plan || null,
      property_type: propertyTypeValue,
      property_subtype: propertySubtypeValue,
      offer: formState.offer || null,
      due_diligence: formState.dueDiligence || null,
      days_for_closing: formState.daysForClosing || null,
      title_company: formState.titleCompany || null,
      escrow_amount: formState.escrowAmount || null,
      payment_type: formState.paymentType || null,
      future_use: formState.futureUse || null,
      commission: formState.commission || null,
      co_buyer_name: formState.co_buyer_name || null,
      co_buyer_last_name: formState.co_buyer_last_name || null,
      co_buyer_email: formState.co_buyer_email || null,
      co_buyer_phone: formState.co_buyer_phone || null,
      seller_a: formState.sellerA || null,
      seller_b: formState.sellerB || null,
      owner_property_type: formState.owner_property_type || null,
      occupancy: formState.occupancy || null,
      current_property_street: formState.current_property_street || null,
      current_property_city: formState.current_property_city || null,
      current_property_state: formState.current_property_state || null,
      current_property_zip: formState.current_property_zip || null,
      notes: formState.notes || null,
      wants_needs: formState.home_features || null,
      down_payment: formState.down_payment || null,
      preferred_neighborhoods: formState.preferred_neighborhoods || null,
      prospect_street: formState.prospect_street || null,
      prospect_city: formState.prospect_city || null,
      prospect_state: formState.prospect_state || null,
      prospect_zip: formState.prospect_zip || null,
      parcel_number: formState.parcel_number || formState.parcelNumber || null,
      acreage: formState.acreage || null,
      min_bedrooms: formState.min_bedrooms || null,
      min_bathrooms: formState.min_bathrooms || null,
    };

    try {
      const result = await apiFetch("editContact", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!result.success) {
        setSnackbar({ show: true, message: `❌ ${result.message}`, type: 'danger' });
      } else {
        setSnackbar({ show: true, message: '✅ Contact updated!', type: 'success' });
        const updatedContact = {
          ...contactData,
          ...formState,
          propertyType: propertyTypeValue,
          propertySubtype: propertySubtypeValue,
          property_type: propertyTypeValue,
          property_subtype: propertySubtypeValue,
          id: contactId,
          first_name: payload.first_name,
          last_name: payload.last_name,
          name: `${payload.first_name} ${payload.last_name}`.trim(),
          company_name: payload.company_name,
          job_title: payload.job_title,
          industry: payload.industry,
          decision_role: payload.decision_role,
          current_solution: payload.current_solution,
          urgency_level: payload.urgency_level,
          next_step: payload.next_step,
          estimated_value: payload.estimated_value,
          prospect_street: payload.prospect_street,
          prospect_city: payload.prospect_city,
          prospect_state: payload.prospect_state,
          prospect_zip: payload.prospect_zip,
          parcel_number: payload.parcel_number,
          acreage: payload.acreage,
          min_bedrooms: payload.min_bedrooms,
          min_bathrooms: payload.min_bathrooms,
          preferred_contact_method: payload.preferred_contact_method,
          priority: payload.priority,
          lead_source: payload.lead_source,
          lead_source_detail: payload.lead_source_detail,
          pain_points: payload.pain_points,
          goals: payload.goals,
          motivation: payload.motivation,
          preapproval_status: payload.preapproval_status,
          prospect_stage: payload.prospect_stage,
          next_follow_up_at: payload.next_follow_up_at,
          timeline_to_purchase: payload.timeline_to_purchase,
          co_buyer_name: payload.co_buyer_name,
          co_buyer_last_name: payload.co_buyer_last_name,
          co_buyer_email: payload.co_buyer_email,
          co_buyer_phone: payload.co_buyer_phone,
          owner_property_type: payload.owner_property_type,
          occupancy: payload.occupancy,
          current_property_street: payload.current_property_street,
          current_property_city: payload.current_property_city,
          current_property_state: payload.current_property_state,
          current_property_zip: payload.current_property_zip,
          best_time_to_contact: payload.time,
          home_features: payload.wants_needs,
          financing_plan: payload.financing_status,
          down_payment: payload.down_payment,
          preferred_neighborhoods: payload.preferred_neighborhoods,
        };
        onSave(updatedContact);
        setTimeout(() => navigate(`/contact/${contactId}`), 1000);
      }
    } catch (err) {
      setSnackbar({ show: true, message: `❌ Failed to save: ${err.message}`, type: 'danger' });
    }
  };

  return (
    <>
    <Snackbar
      show={snackbar.show}
      message={snackbar.message}
      type={snackbar.type}
      onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
    />
    <form onSubmit={handleSubmit} className="container">
      <div className="row">
        {/* Basic Fields */}
        <div className="col-md-3 mb-3">
          <label htmlFor="first_name" className="form-label">First Name</label>
          <input
            type="text"
            className="form-control"
            id="first_name"
            name="first_name"
            value={formState.first_name || ''}
            onChange={handleInputChange}
          />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="last_name" className="form-label">Last Name</label>
          <input
            type="text"
            className="form-control"
            id="last_name"
            name="last_name"
            value={formState.last_name || ''}
            onChange={handleInputChange}
          />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            id="email"
            name="email"
            value={formState.email || ''}
            onChange={handleInputChange}
          />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="phone" className="form-label">Phone</label>
          <input
            type="tel"
            className="form-control"
            id="phone"
            name="phone"
            value={formState.phone || ''}
            onChange={handleInputChange}
          />
        </div>

        {/* Prospecting Property */}
        {realtorView && (extraFields.includes('prospect_property') || prospectFieldsFilled) && (
          <>
            <div className="col-12 mt-2">
              <h5>Prospecting Property</h5>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Street</label>
              <input
                type="text"
                className="form-control"
                name="prospect_street"
                value={formState.prospect_street || ''}
                onChange={handleInputChange}
                placeholder="123 Main St"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-control"
                name="prospect_city"
                value={formState.prospect_city || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-2 mb-3">
              <label className="form-label">State</label>
              <input
                type="text"
                className="form-control"
                name="prospect_state"
                value={formState.prospect_state || ''}
                onChange={handleInputChange}
                maxLength={2}
              />
            </div>
            <div className="col-md-1 mb-3">
              <label className="form-label">Zip</label>
              <input
                type="text"
                className="form-control"
                name="prospect_zip"
                value={formState.prospect_zip || ''}
                onChange={handleInputChange}
                maxLength={10}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Parcel / APN</label>
              <input
                type="text"
                className="form-control"
                name="parcel_number"
                value={formState.parcel_number || formState.parcelNumber || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Acreage</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                name="acreage"
                value={formState.acreage || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Min Bedrooms</label>
              <input
                type="number"
                className="form-control"
                name="min_bedrooms"
                value={formState.min_bedrooms || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Min Bathrooms</label>
              <input
                type="number"
                className="form-control"
                name="min_bathrooms"
                value={formState.min_bathrooms || ''}
                onChange={handleInputChange}
              />
            </div>
          </>
        )}

        {/* Mailing Address */}
        {[
          { name: 'mailAddress', label: 'Street Address' },
          { name: 'mailCity', label: 'Mailing City' },
          { name: 'mailSt', label: 'Mailing State' },
          { name: 'mailZip', label: 'Mailing Zip' }
        ].map((field, idx) => {
          if (field.name === 'mailSt') {
            const stateOptions = dropdownOptions.mailSt || [];
            const isCustomState =
              customFieldOverrides.mailSt ||
              (formState.mailSt && !optionMatchesValue(stateOptions, formState.mailSt));
            const selectValue = isCustomState ? CUSTOM_OPTION_VALUE : (formState.mailSt || '');
            return (
              <div className="col-md-3 mb-3" key={idx}>
                <label className="form-label">{field.label}</label>
                <select
                  className="form-select"
                  name="mailSt"
                  value={selectValue}
                  onChange={handleInputChange}
                >
                  <option value="">Select state</option>
                  {stateOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                  <option value={CUSTOM_OPTION_VALUE}>Custom</option>
                </select>
                {isCustomState && (
                  <input
                    type="text"
                    className="form-control mt-2"
                    name="mailStCustom"
                    value={formState.mailSt || ''}
                    onChange={(e) => handleCustomValueChange('mailSt', e.target.value)}
                    placeholder="Enter state or province"
                  />
                )}
              </div>
            );
          }

          return (
            <div className="col-md-3 mb-3" key={idx}>
              <label className="form-label">{field.label}</label>
              <input
                type="text"
                className="form-control"
                name={field.name}
                value={formState[field.name] || ''}
                onChange={handleInputChange}
              />
            </div>
          );
        })}

        {/* Role */}
        <div className="col-md-4 mb-3">
          <label className="form-label">Role Type</label>
          <select className="form-select" value={roleType} onChange={handleRoleChange}>
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {renderFieldGroup('Contact Qualification', [
          'best_time_to_contact',
          'lead_source',
          'preferred_contact_method',
          'prospect_stage',
          'next_follow_up_at',
          'pain_points',
          'goals',
          'budget',
          'timeline_to_purchase',
          'motivation',
          ...(realtorView ? ['preapproval_status'] : []),
          'llcs',
          'notes',
        ])}

        {renderFieldGroup('Sales Profile', [
          'company_name',
          'job_title',
          'industry',
          'decision_role',
          'urgency_level',
          'estimated_value',
          'next_step',
          'current_solution',
        ])}

        {realtorView && renderFieldGroup('Current Property Owned', [
          'owner_property_type',
          'occupancy',
          'current_property_street',
          'current_property_city',
          'current_property_state',
          'current_property_zip',
        ])}

        {renderFieldGroup('Co-Buyer', [
          'co_buyer_name',
          'co_buyer_last_name',
          'co_buyer_email',
          'co_buyer_phone',
        ])}

        {realtorView && renderFieldGroup('Search Criteria', [
          'propertyType',
          'home_features',
          'financing_plan',
          'down_payment',
          'preferred_neighborhoods',
        ])}

        {renderFieldGroup('Transaction Details', [
          'offer',
          'dueDiligence',
          'daysForClosing',
          'titleCompany',
          'escrowAmount',
          'commission',
          'term',
          'link',
        ])}

        {/* Field Selector */}
        {availableFieldsToAdd.length > 0 && (
          <div className="col-12 mb-3">
            <label className="form-label">Add More Fields</label>
            <div className="border rounded p-2" style={{ maxHeight: '150px', overflowY: 'scroll' }}>
              {availableFieldsToAdd.map((field) => (
                <div key={field.name} className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={field.name}
                    checked={extraFields.includes(field.name)}
                    onChange={() => handleExtraFieldToggle(field.name)}
                  />
                  <label className="form-check-label ms-1" htmlFor={field.name}>
                    {field.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <button type="submit" className="btn btn-primary w-100 mt-3">Save</button>
    </form>
    </>
  );
}

export default EditContactForm;
